import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/dashboard/shared/PageHeader";
import { DashboardHead } from "@/components/SEO/DashboardHead";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { isPaidAccess } from "@/lib/plan";
import { toast } from "sonner";
import { Loader2, Upload } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

type Row = {
  name: string;
  basePrice: string | number;
  description?: string;
  category?: string;
  sku?: string;
  inventory?: string | number;
  status?: string;
};

function parseCsv(text: string): Row[] {
  const lines = text
    .trim()
    .split(/\r?\n/)
    .filter(Boolean);
  if (lines.length < 2) return [];
  const header = lines[0].split(",").map((h) => h.trim().toLowerCase());
  const rows: Row[] = [];
  for (let i = 1; i < lines.length; i++) {
    const cells = lines[i].split(",").map((c) => c.trim());
    const row: Record<string, string> = {};
    header.forEach((h, idx) => {
      row[h] = cells[idx] ?? "";
    });
    if (row.name && row.baseprice !== undefined) {
      rows.push({
        name: row.name,
        basePrice: row.baseprice,
        description: row.description,
        category: row.category,
        sku: row.sku,
        inventory: row.inventory,
        status: row.status,
      });
    }
  }
  return rows;
}

export function BulkUploadPage() {
  const { user, currentStore } = useAuth();
  const queryClient = useQueryClient();
  const [raw, setRaw] = useState("");
  const ownerPaid =
    currentStore?.isOwner && isPaidAccess(user);

  const mutation = useMutation({
    mutationFn: async (rows: Row[]) => {
      const { data } = await api.post("/api/products/bulk", { rows });
      return data as {
        created: number;
        failed: number;
        errors: { index: number; message: string }[];
      };
    },
    onSuccess: (d) => {
      toast.success(`Created ${d.created} products`);
      if (d.failed > 0) {
        toast.message(`${d.failed} rows failed`, {
          description: d.errors
            .slice(0, 5)
            .map((e) => `Row ${e.index + 1}: ${e.message}`)
            .join("\n"),
        });
      }
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setRaw("");
    },
    onError: (e: unknown) => {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Upload failed";
      toast.error(msg);
    },
  });

  if (!ownerPaid) {
    return (
      <div className="space-y-6">
        <DashboardHead pageName="Bulk upload" />
        <PageHeader title="Bulk product upload" />
        <Alert>
          <AlertTitle>Premium required</AlertTitle>
          <AlertDescription className="flex flex-col gap-2">
            <span>
              Bulk upload is available on Premium or Custom plans for store
              owners.
            </span>
            <Button asChild size="sm" className="w-fit">
              <Link to="/dashboard/settings#billing">View billing</Link>
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const handleSubmit = () => {
    const rows = parseCsv(raw);
    if (rows.length === 0) {
      toast.error("Add a header row and data rows (see placeholder)");
      return;
    }
    mutation.mutate(rows);
  };

  return (
    <div className="space-y-6">
      <DashboardHead pageName="Bulk upload" />
      <PageHeader
        title="Bulk product upload"
        subtitle="Paste CSV with columns: name, basePrice, and optional description, category, sku, inventory, status"
      />

      <Card className="p-6 space-y-4">
        <div>
          <Label htmlFor="csv">CSV content</Label>
          <Textarea
            id="csv"
            className="mt-2 font-mono text-sm min-h-[220px]"
            placeholder={`name,basePrice,description,category,sku,inventory,status
Sample Product,2500,Nice item,General,SKU1,10,draft
Another,1500,,,,5,active`}
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
          />
        </div>
        <Button
          onClick={handleSubmit}
          disabled={mutation.isPending || !raw.trim()}
          className="gap-2"
        >
          {mutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          Upload products
        </Button>
      </Card>
    </div>
  );
}
